/**
 * @fileoverview Hook de Actions Layer para validación de archivos del Template Carousel
 * @module useFileValidation
 * @category Hook - Actions Layer
 * @since 1.0.0
 * @version 3.2.0
 */

import { useCallback, useMemo } from 'react';

/**
 * Hook de Actions Layer para validación de archivos del Template Carousel.
 */
const useFileValidation = (fileConfig) => {

  /**
   * Valida un archivo individual contra todas las reglas configuradas.
   */
  const validateFile = useCallback(async (file, options = {}) => {
    const { skipCustom = false, detailed = false } = options;

    // Validación de entrada
    if (!file || !(file instanceof File)) {
      const error = 'El archivo proporcionado no es válido';
      return {
        isValid: false,
        error,
        errorType: 'input',
        details: { providedValue: file }
      };
    }

    if (!fileConfig) {
      const error = 'Configuración de archivos no disponible';
      return {
        isValid: false,
        error,
        errorType: 'config',
        details: null
      };
    }

    try {
      // Validación de tamaño
      if (fileConfig.maxSize && file.size > fileConfig.maxSize) {
        const formattedMax = formatFileSize(fileConfig.maxSize);
        const formattedActual = formatFileSize(file.size);
        
        const error = `El archivo es demasiado grande. Máximo permitido: ${formattedMax}, actual: ${formattedActual}`;
        
        return {
          isValid: false,
          error,
          errorType: 'size',
          details: detailed ? {
            maxSize: fileConfig.maxSize,
            actualSize: file.size,
            fileName: file.name
          } : null
        };
      }

      // Validación de tipo MIME
      if (fileConfig.validTypes && !fileConfig.validTypes.includes(file.type)) {
        const allowedTypes = fileConfig.validTypes.join(', ');
        const error = `Tipo de archivo no permitido. Tipos válidos: ${allowedTypes}. Actual: ${file.type}`;
        
        return {
          isValid: false,
          error,
          errorType: 'type',
          details: detailed ? {
            validTypes: fileConfig.validTypes,
            actualType: file.type,
            fileName: file.name
          } : null
        };
      }

      // Validaciones personalizadas
      if (!skipCustom && fileConfig.validator && typeof fileConfig.validator === 'function') {
        try {
          const customResult = await fileConfig.validator(file);
          
          if (customResult !== true) {
            const error = typeof customResult === 'string' 
              ? customResult 
              : 'El archivo no cumple con los requisitos personalizados';
            
            return {
              isValid: false,
              error,
              errorType: 'custom',
              details: detailed ? {
                validatorResult: customResult,
                fileName: file.name
              } : null
            };
          }
        } catch (validatorError) {
          const error = `Error en validación personalizada: ${validatorError.message}`;
          
          return {
            isValid: false,
            error,
            errorType: 'custom',
            details: detailed ? {
              validatorError: validatorError.message,
              fileName: file.name
            } : null
          };
        }
      }

      // Archivo válido
      return {
        isValid: true,
        error: null,
        errorType: null,
        details: detailed ? getFileMetadata(file) : null
      };

    } catch (error) {
      return {
        isValid: false,
        error: `Error durante la validación: ${error.message}`,
        errorType: 'system',
        details: detailed ? {
          systemError: error.message,
          fileName: file?.name
        } : null
      };
    }
  }, [fileConfig]);

  /**
   * Extrae metadatos completos de un archivo.
   */
  const getFileMetadata = useCallback((file) => {
    if (!file || !(file instanceof File)) {
      return null;
    }

    // Extraer extensión del archivo
    const nameparts = file.name.split('.');
    const extension = nameparts.length > 1 ? `.${nameparts.pop().toLowerCase()}` : null;

    // Determinar categoría basada en tipo MIME
    let category = 'document';
    if (file.type.startsWith('image/')) {
      category = 'image';
    } else if (file.type.startsWith('video/')) {
      category = 'video';
    } else if (file.type.startsWith('audio/')) {
      category = 'audio';
    }

    return {
      name: file.name,
      type: file.type,
      size: file.size,
      formattedSize: formatFileSize(file.size),
      lastModified: file.lastModified,
      extension,
      category
    };
  }, []);

  /**
   * Formatea el tamaño de archivo en formato legible.
   */
  const formatFileSize = useCallback((bytes, decimals = 1) => {
    if (bytes === 0) return '0 B';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }, []);

  /**
   * Objeto de retorno memoizado para optimizar performance.
   */
  return useMemo(() => ({
    validateFile,
    getFileMetadata,
    formatFileSize
  }), [
    validateFile,
    getFileMetadata,
    formatFileSize
  ]);
};

export default useFileValidation;